
  import { computed, defineComponent, ref, onMounted, onBeforeUnmount, nextTick } from 'vue';
  import { useRouter } from 'vue-router';
  import { useStore } from 'vuex';
  import { useDebounceFn } from '@vueuse/core';
  import { Modal } from 'bootstrap';

  export default defineComponent({
    name: 'SearchComponent',
    setup() {
      const isFocused = ref(false);
      const searchQuery = ref('');
      const router = useRouter();
      const store = useStore();
      const isLoading = ref(false);
      const selectMode = ref('start');
      const searchResultsINN = ref<any[]>([]);
      const searchResultsIP = ref<any[]>([]);
      const isMobile = ref(false);
      const mobileModalOpen = ref(false);
      const mobileInput = ref<HTMLInputElement | null>(null);
      const accessDeniedStatus = ref<number | null>(null);

      const accessDeniedTitle = computed(() =>
        accessDeniedStatus.value === 401 ? 'Войдите для поиска' : 'Нужен оплаченный тариф'
      );
      const accessDeniedDescription = computed(() =>
        accessDeniedStatus.value === 401
          ? 'Поиск и карточки компаний доступны после авторизации.'
          : 'Информация по компаниям доступна только пользователям с активным тарифом.'
      );

      const resetSearchResults = () => {
        searchResultsINN.value = [];
        searchResultsIP.value = [];
        accessDeniedStatus.value = null;
      };

      const visibleSearchResults = computed(() => {
        if (selectMode.value === 'ip') {
          return searchResultsIP.value;
        }

        if (searchResultsINN.value.length || selectMode.value === 'inn') {
          return searchResultsINN.value;
        }

        return searchResultsIP.value;
      });

      const syncMobile = () => {
        isMobile.value = window.innerWidth <= 768;
      };

      const openMobileModal = () => {
        mobileModalOpen.value = true;
        document.body.style.overflow = 'hidden';
        nextTick(() => mobileInput.value?.focus());
      };

      const closeMobileModal = () => {
        mobileModalOpen.value = false;
        searchQuery.value = '';
        resetSearchResults();
        document.body.style.overflow = '';
      };

      const closeSearchSurface = () => {
        isFocused.value = false;
        mobileModalOpen.value = false;
        document.body.style.overflow = '';
      };

      onMounted(() => {
        syncMobile();
        window.addEventListener('resize', syncMobile);
      });

      onBeforeUnmount(() => {
        window.removeEventListener('resize', syncMobile);
        document.body.style.overflow = '';
      });
      const isShortTextQuery = (value: string) => {
        const normalized = value.trim();
        return !/^\d+$/.test(normalized) && normalized.length < 4;
      };

      const getCompanyId = (company: any) =>
        company?.['ИНН'] || company?.['ОГРН'] || company?.['ОГРНИП'] || company?.uscc || company?.company_name_en || '';

      const getCompanyName = (company: any) =>
        company?.['НаимСокр'] ||
        company?.['НаимПолн'] ||
        company?.['ФИО'] ||
        company?.company_name_en ||
        'Без названия';

      const getCompanyMeta = (company: any) => {
        const meta: string[] = [];

        if (company?.['ИНН']) {
          meta.push(`ИНН ${company['ИНН']}`);
        }

        if (company?.['ОГРН'] || company?.['ОГРНИП']) {
          meta.push(`ОГРН ${company['ОГРН'] || company['ОГРНИП']}`);
        }

        if (company?.['ЮрАдрес']?.['АдресРФ']) {
          meta.push(company['ЮрАдрес']['АдресРФ']);
        }

        if (company?.country_province_city) {
          meta.push(company.country_province_city);
        }

        return meta.join(' · ');
      };

      const goToCompany = (companyId: string) => {
        if (!companyId) return;
        isFocused.value = false;
        const originalQuery = searchQuery.value;
        searchQuery.value = '';
        router.push({
          path: '/counter-parties/' + companyId,
          query: { q: originalQuery },
        });
      };

      const openAuthModal = () => {
        closeSearchSurface();
        const authModal = document.getElementById('authModal');
        if (authModal) {
          new Modal(authModal).show();
        }
      };

      const goToPricing = () => {
        closeSearchSurface();
        searchQuery.value = '';
        resetSearchResults();
        router.push({ path: '/', hash: '#pricing-section' });
      };

      const searchCompany = useDebounceFn(async () => {
        if (!searchQuery.value.trim()) {
          isLoading.value = false;
          resetSearchResults();
          return;
        }

        if (isShortTextQuery(searchQuery.value)) {
          isLoading.value = false;
          resetSearchResults();
          return;
        }

        isFocused.value = true;
        accessDeniedStatus.value = null;

        try {
          const res = await store.dispatch('companies/searchCompanies', searchQuery.value);
          selectMode.value = 'start';

          if (res && (res.org || res.ent)) {
            searchResultsINN.value = Array.isArray(res.org) ? res.org : [];
            searchResultsIP.value = Array.isArray(res.ent) ? res.ent : [];
          } else if (Array.isArray(res)) {
            searchResultsINN.value = res;
            searchResultsIP.value = [];
          } else if (res && typeof res === 'object' && res.company_name_en) {
            searchResultsINN.value = [res];
            searchResultsIP.value = [];
          } else {
            searchResultsINN.value = [];
            searchResultsIP.value = [];
          }
        } catch (_err: any) {
          searchResultsINN.value = [];
          searchResultsIP.value = [];
          const responseStatus = _err?.response?.status;
          accessDeniedStatus.value = responseStatus === 401 || responseStatus === 403 ? responseStatus : null;
        } finally {
          isLoading.value = false;
        }
      }, 700);

      const onSearch = () => {
        if (searchQuery.value.length > 0) {
          isLoading.value = true;
          searchCompany();
        } else {
          isLoading.value = false;
          resetSearchResults();
        }
      };

      return {
        goToCompany,
        onSearch,
        searchQuery,
        isFocused,
        isLoading,
        selectMode,
        searchResultsIP,
        searchResultsINN,
        visibleSearchResults,
        accessDeniedStatus,
        accessDeniedTitle,
        accessDeniedDescription,
        isMobile,
        mobileModalOpen,
        mobileInput,
        openMobileModal,
        closeMobileModal,
        openAuthModal,
        goToPricing,
        getCompanyId,
        getCompanyName,
        getCompanyMeta,
      };
    },
  });
